/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Modal, Table } from '../../../UiKit';
import { EmptyState } from '../../commons';
import { agentActionsModel } from '../../../api';
import { InvalidSearchImg } from '../../../images/images';
import {
  REFUND_ENTITY,
  BmoneySkeletonRows,
  BMONEY_API,
  userZMoneyHeadings,
  getUserTxnRows,
} from './utils';
import Button from '../../ComponentsV1/Button';
import { RefreshCcw } from 'lucide-react';
import { Skeleton } from '../../ComponentsV1/Skeleton/Skeleton';
import { useMutationCall } from '../../../api/react-client';
import toast from 'react-hot-toast';
import { ToolTip } from '../../ComponentsV1/ToolTip/Tooltip';
import InputV1 from '../../ComponentsV1/InputV1';
import { TableV1 } from '../../ComponentsV1/Table/Table';

export const UserBMoneyTxns = ({ orderId = null, userId = null }) => {
  const getBMoneyDetailsRef = useRef();
  const [userBMoneyTxns, setUserBMoneyTxns] = useState(null);
  const [isRefundAllowed, setIsRefundAllowed] = useState(false);
  const [popupData, setpopupData] = useState({});
  const [selectedReason, setSelectedReason] = useState(null);

  const [refreshRotation, setRefreshRotation] = useState(0);
  const rotationStep = 540;

  // When popup is closed, reset selectedReason and remove reasonCode from popupData
  useEffect(() => {
    if (!popupData.isVisible) {
      setSelectedReason(null);
      setpopupData((prev) => {
        const { reasonCode, txnId, ...rest } = prev;
        return rest;
      });
    }
  }, [popupData.isVisible]);

  const getBMoneyDetails = useCallback(() => {
    setRefreshRotation((prev) => prev + rotationStep);

    let queryStrings = {
      order_id: orderId,
      user_id: userId,
    };

    agentActionsModel.getBMoneyDetails(queryStrings).then((response) => {
      if (response) {
        setUserBMoneyTxns(response);
        setIsRefundAllowed(response.allow_refunds);
      }
    });
  }, [userId, orderId]);

  getBMoneyDetailsRef.current = getBMoneyDetails;

  // Fetch B-money transactions on first render and then every 5 seconds
  useEffect(() => {
    getBMoneyDetails();
    const interval = setInterval(() => {
      if (getBMoneyDetailsRef.current) getBMoneyDetailsRef.current();
    }, 5000);
    return () => clearInterval(interval);
  }, [getBMoneyDetails]);

  const { mutate: refundBmoney } = useMutationCall(
    {
      onSuccess: () => {
        toast.success('Refunded Successfully');
        setpopupData({});
      },
    },
    popupData?.refundEntity === REFUND_ENTITY.WALLET
      ? BMONEY_API.REFUND_WALLET
      : BMONEY_API.REFUND_TRANSACTION,
    [],
    {
      doNotShowSuccessToast: true,
    },
  );

  if (!userBMoneyTxns) {
    return <Skeleton rows={BmoneySkeletonRows} className='tw-mt-6' />;
  }

  if (!userBMoneyTxns?.transactions?.length) {
    return (
      <EmptyState
        img={InvalidSearchImg}
        heading='No Transactions available!'
        isPop={true}
      />
    );
  }

  const Refund = () => {
    let payload = {};
    if (popupData?.refundEntity === REFUND_ENTITY.WALLET) {
      payload = {
        user_id: userId,
        reason: popupData?.reasonCode,
      };
    } else if (popupData?.refundEntity === REFUND_ENTITY.TRANSACTION) {
      payload = {
        user_id: userId,
        reason: popupData?.reasonCode,
        transaction_id: popupData?.txnId,
      };
    }
    if (!payload.reason) {
      toast.error('Please select a reason');
      return;
    }
    refundBmoney(payload);
  };

  return (
    <div className='userWalletDetails modalContent'>
      <div className='tw-flex tw-items-center tw-mb-5 tw-justify-between'>
        <div className='tw-flex tw-items-center tw-leading-none tw-w-fit tw-border-solid tw-rounded-lg tw-border-grey-300/80 tw-bg-grey-100/50 tw-border-[1px]'>
          <div className='tw-p-4 tw-text-[1.4rem] tw-font-medium tw-border-solid tw-border-y-0 tw-border-l-0 tw-border-grey-300/80 tw-border-[1px]'>
            Current Balance
          </div>
          <div className='tw-p-4 tw-font-medium tw-text-base-green'>
            ₹{userBMoneyTxns?.wallet_details?.balance}
          </div>
        </div>

        <div className='tw-flex tw-gap-3'>
          {/* Refresh Txns */}
          <ToolTip
            content={
              <div className='tw-flex tw-flex-col tw-gap-2 tw-items-center'>
                <span className='tw-text-[1.4rem] tw-font-medium tw-leading-none'>
                  Refresh Transactions
                </span>
                <span className='tw-text-zinc-200 tw-text-[1.2rem] tw-text-center tw-leading-none'>
                  (auto refreshes every 5 seconds)
                </span>
              </div>
            }
          >
            <div
              className='tw-cursor-pointer tw-text-base-green tw-flex tw-items-center tw-aspect-square tw-justify-center tw-rounded-[8px] tw-bg-grey-200/40 hover:tw-bg-grey-200/60 tw-transition-all tw-ease-in-out'
              onClick={getBMoneyDetails}
            >
              <RefreshCcw
                size={16}
                strokeWidth={2.3}
                className='tw-ease-in-out tw-m-[0.9rem] tw-aspect-square'
                style={{
                  transition: 'transform 0.7s cubic-bezier(0.4,0.1,0.3,1)',
                  transform: `rotate(-${refreshRotation}deg)`,
                }}
              />
            </div>
          </ToolTip>
          {/* Refund Wallet */}
          {isRefundAllowed && (
            <Button
              onClick={() => {
                setSelectedReason(null);
                setpopupData({
                  isVisible: true,
                  popupTitle: 'Refund Wallet',
                  refundEntity: REFUND_ENTITY.WALLET,
                  reasonCode: undefined,
                  txnId: undefined,
                });
              }}
              title={
                <div className='tw-leading-none tw-flex tw-items-center tw-gap-2'>
                  Refund Wallet
                </div>
              }
            />
          )}
        </div>
      </div>

      {/* Transactions Table */}
      {userBMoneyTxns.transactions ? (
        <TableV1
          columns={userZMoneyHeadings}
          rows={getUserTxnRows(
            userBMoneyTxns.transactions,
            setpopupData,
            isRefundAllowed,
          )}
        />
      ) : null}

      <Modal
        isVisible={popupData.isVisible}
        type='medium'
        title={
          <div className='tw-font-medium tw-text-[2rem]'>
            {popupData.popupTitle}
          </div>
        }
        handleClose={() => {
          setSelectedReason(null);
          setpopupData((prev) => ({
            ...prev,
            isVisible: false,
            reasonCode: undefined,
            txnId: undefined,
          }));
        }}
        component={
          <div className='tw-h-full tw-flex-col tw-justify-between'>
            <InputV1
              isSearchable={true}
              multiple={false}
              options={userBMoneyTxns?.refund_reason_codes}
              className='tw-w-full'
              placeholder={'Select refund reason'}
              closeOnSelect
              value={selectedReason}
              onChange={(selectedValue) => {
                setSelectedReason(selectedValue);
                setpopupData((prev) => ({
                  ...prev,
                  reasonCode: selectedValue,
                }));
              }}
            />
            <div className='tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-12'>
              <Button
                type='secondary'
                title={'Cancel'}
                onClick={() => {
                  setSelectedReason(null);
                  setpopupData((prev) => ({
                    ...prev,
                    isVisible: false,
                    reasonCode: undefined,
                    txnId: undefined,
                  }));
                }}
              />
              <Button title={'Refund'} onClick={Refund} />
            </div>
          </div>
        }
      />
    </div>
  );
};
